import { Injectable } from '@angular/core';
import { State, Selector, StateContext, Action } from '@ngxs/store';
import { StateNames } from './models/state-names.enum';
import {
  DisableGPSFollow,
  DisableGPSWatch,
  EnableGpsWatch,
  MaintenanceLayers,
  MapMode,
  MapStateModel,
  ResetCenterAndZoom,
  ResetMapState,
  SetCenterAndZoom,
  SetGeolocationPermission,
  SetMapMode,
  UpdateLayers,
} from './models/map.state.model';

const defaultMapState = {
  mapMode: MapMode.inspect,
  center: [],
  zoom: 19,
  showGPSLocation: false,
  followGPSLocation: false,
  geolocationPermission: 'unknown' as const,
  layers: {
    // Empty means the default basemap, see withDefaultBasemap()
    basemaps: [],
    klicLayerFilter: [],
    maintenanceLayers: {
      eisVoorzorgsmaatregel: true,
      klicMeldingenInProject: true,
      extraDetailInfo: true,
      annotations: false,
      maatvoeringen: false,
      dieptes: true,
      designLayers: {},
      risk: false,
      veiligGraven: true,
      risicoInventarisatie: false,
    },
    isDefault: true,
  },
};

@State<MapStateModel>({
  name: StateNames.map_v6,
  defaults: defaultMapState,
})
@Injectable()
export class MapState {
  @Selector([MapState])
  static mapMode(state: MapStateModel) {
    return state.mapMode;
  }

  @Selector([MapState])
  static center(state: MapStateModel) {
    return state.center;
  }

  @Selector([MapState])
  static zoom(state: MapStateModel) {
    return state.zoom;
  }

  @Selector([MapState])
  static layers(state: MapStateModel) {
    return state.layers;
  }

  @Selector([MapState.layers])
  static basemaps(visibleLayers: {
    basemaps: string[];
    klicLayerFilter: string[];
    maintenanceLayers: MaintenanceLayers;
  }) {
    return visibleLayers.basemaps;
  }

  @Selector([MapState.layers])
  static klicLayerFilter(visibleLayers: {
    basemaps: string[];
    klicLayerFilter: string[];
    maintenanceLayers: MaintenanceLayers;
  }) {
    return visibleLayers.klicLayerFilter;
  }

  @Selector([MapState.layers])
  static maintenanceLayers(visibleLayers: {
    basemaps: string[];
    klicLayerFilter: string[];
    maintenanceLayers: MaintenanceLayers;
  }) {
    return visibleLayers.maintenanceLayers;
  }

  @Selector([MapState])
  static showGPSLocation(state: MapStateModel) {
    return state.showGPSLocation;
  }

  @Selector([MapState])
  static followGPSLocation(state: MapStateModel) {
    return state.followGPSLocation;
  }

  @Selector([MapState])
  static geolocationPermission(state: MapStateModel) {
    return state.geolocationPermission;
  }

  constructor() {}

  @Action(SetMapMode)
  setMapMode(ctx: StateContext<MapStateModel>, action: SetMapMode) {
    ctx.patchState({
      mapMode: action.payload.mapMode,
    });
  }

  @Action(SetCenterAndZoom)
  setCenterAndZoom(ctx: StateContext<MapStateModel>, action: SetCenterAndZoom) {
    ctx.patchState({
      center: action.payload.center,
      zoom: action.payload.zoom,
    });
  }

  @Action(UpdateLayers)
  updateLayers(ctx: StateContext<MapStateModel>, action: UpdateLayers) {
    const currentLayers = ctx.getState().layers;
    ctx.patchState({
      layers: {
        basemaps: action.payload.basemaps || currentLayers.basemaps,
        klicLayerFilter: action.payload.klicLayerFilter || currentLayers.klicLayerFilter,
        maintenanceLayers: {
          ...currentLayers.maintenanceLayers,
          ...action.payload.maintenanceLayers,
        },
        isDefault: false,
      },
    });
  }

  @Action(ResetCenterAndZoom)
  resetCenterAndZoom(ctx: StateContext<MapStateModel>) {
    ctx.patchState({
      center: defaultMapState.center,
      zoom: defaultMapState.zoom,
    });
  }

  @Action(EnableGpsWatch)
  enableGpsWatch(ctx: StateContext<MapStateModel>, action: EnableGpsWatch) {
    ctx.patchState({
      showGPSLocation: true,
      followGPSLocation: action.payload.followGPSLocation,
    });
  }

  @Action(DisableGPSFollow)
  disableGPSFollow(ctx: StateContext<MapStateModel>) {
    ctx.patchState({
      followGPSLocation: false,
    });
  }

  @Action(DisableGPSWatch)
  disableGPSWatch(ctx: StateContext<MapStateModel>) {
    ctx.patchState({
      showGPSLocation: false,
      followGPSLocation: false,
    });
  }

  @Action(ResetMapState)
  resetMapState(ctx: StateContext<MapStateModel>) {
    const state = ctx.getState();
    ctx.setState({
      ...defaultMapState,
      geolocationPermission: state.geolocationPermission,
      layers: {
        ...defaultMapState.layers,
        basemaps: state.layers.basemaps,
        maintenanceLayers: {
          ...state.layers.maintenanceLayers,
          eisVoorzorgsmaatregel: true,
        },
      },
    });
  }

  @Action(SetGeolocationPermission)
  setGeolocationPermission(ctx: StateContext<MapStateModel>, action: SetGeolocationPermission) {
    ctx.patchState({
      geolocationPermission: action.payload.status,
    });
  }
}
